import { getErrorDetails, shouldRetry } from "./errors.ts";
import { setPersistenceStatus } from "./persistenceStatus.ts";

const DEFAULT_MAX_RETRIES = 5;

interface Options {
  maxRetries?: number;
  startedAt?: Date;
}

export const persistWithRetries = async (
  fn: () => Promise<unknown>,
  { maxRetries = DEFAULT_MAX_RETRIES, startedAt = new Date() }: Options = {},
) => {
  const attemptNow = async () => {
    setPersistenceStatus({ type: "working" });

    for (let attemptNumber = 1; attemptNumber <= maxRetries + 1; attemptNumber++) {
      try {
        const result = await fn();
        setPersistenceStatus({ type: "idle" });
        return result;
      } catch (error) {
        if (!shouldRetry(error)) {
          // return as not retrying aka going back around loop;
          return new Promise((_, reject) =>
            setPersistenceStatus({
              type: "failed",
              errorDetails: getErrorDetails(error, startedAt),
              dismiss: () => {
                setPersistenceStatus({ type: "idle" });
                reject(error); // return this to caller so optimistic updates rolled back etc
              },
            }),
          );
        }

        if (attemptNumber > maxRetries) {
          // return as final error so not going to retry aka go back around loop;
          return new Promise<unknown>((resolve, reject) => {
            setPersistenceStatus({
              type: "retriesExhausted",
              attemptCount: attemptNumber,
              errorDetails: getErrorDetails(error, startedAt),
              dismiss: () => {
                setPersistenceStatus({ type: "idle" });
                reject(error); // return this to caller so optimistic updates rolled back etc
              },
              retry: () => {
                attemptNow() // go back to top
                  .then(resolve)
                  .catch(reject);
              },
            });
          });
        }

        // wait for the exponential backoff delay before retrying but user can skip delay
        await new Promise<void>((resolve) => {
          const retryNow = () => resolve(); // this is to skip the timer below. exactly what retry now means
          const delayMs = calculateBackoffDelay(attemptNumber);
          setPersistenceStatus({ type: "retrying", attemptCount: attemptNumber, retryInMs: delayMs, retryNow });
          return setTimeout(resolve, delayMs);
        });
      }
    }
  };

  return await attemptNow();
};

/**** copilot code via claude sonnet 3.7 */

interface BackoffDelayOptions {
  /** Base delay in milliseconds (default: 1000ms) */
  baseDelay?: number;
  /** Maximum delay in milliseconds (default: 30000ms) */
  maxDelay?: number;
  /** Whether to add randomness to avoid thundering herd problem (default: true) */
  jitter?: boolean;
}

/**
 * Calculates exponential backoff delay in milliseconds
 * @param attemptNumber The current attempt number (starts at 1)
 * @param options Configuration options for the backoff algorithm
 * @returns Delay time in milliseconds
 */
const calculateBackoffDelay = (
  attemptNumber: number,
  { baseDelay = 1000, maxDelay = 30000, jitter = true }: BackoffDelayOptions = {},
): number => {
  // Calculate exponential backoff: baseDelay * 2^(attemptNumber-1)
  const exponentialDelay = baseDelay * Math.pow(2, attemptNumber - 1);

  // Apply maximum constraint
  const cappedDelay = Math.min(exponentialDelay, maxDelay);

  // Add jitter to avoid all clients retrying simultaneously
  if (jitter) {
    // Add random jitter between 0-25% of the delay
    return Math.floor(cappedDelay * (0.75 + Math.random() * 0.25));
  }

  return cappedDelay;
};
